import React, { useState } from 'react';
import { Box, TextField, MenuItem, Grid } from '@mui/material';
import { InventoryStatus, AllocationOption } from 'common/modules/inventory';
import ActionDialog from 'common/components/ActionDialog';
import { useTranslation } from 'services/localization';
import { getTypedKeys } from 'common/utils/objects';
import { InventoryStatuses } from 'common/modules/inventory/constants';
import { getInventoryStatusTranslation } from '../../utils';
import {
	allocationOptionValues,
	getInventoryAllocationOptionTranslation,
} from 'views/Inventory/utils';

interface Props {
	onSave: (newStatus: InventoryStatus, qty: number, allocationKey: AllocationOption) => void;
	onCancel: () => void;
}

const inventoryStatusesList = getTypedKeys(InventoryStatuses);

const AddItemsModal = (props: Props) => {
	const { t } = useTranslation();
	const [newStatus, setNewStatus] = useState<InventoryStatus>(inventoryStatusesList[0]);
	const [quantity, setQuantity] = useState<number>(1);
	const [allocationValue, setAllocationValue] = useState<AllocationOption>('rental');

	const handleAdd = () => {
		props.onSave(newStatus, quantity, allocationValue);
	};

	const handleQuantityChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
		const value = Number(e.target.value);
		if (isNaN(value)) return;
		setQuantity(value);
	};

	const handleStatusChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
		const value = e.target.value as InventoryStatus;
		setNewStatus(value);
	};

	return (
		<ActionDialog
			title={t('inventory.article.itemsAddTitle', 'Add items')}
			headerVariant="contained"
			btnActions={{
				primary: {
					text: t('common:actions.add'),
					disabled: !quantity,
					onClick: handleAdd,
				},
				secondary: {
					text: t('common:actions.cancel'),
					onClick: props.onCancel,
				},
			}}
			onClose={props.onCancel}
		>
			<Box mt={2}>
				<Box mt={4} mb={2}>
					<Grid container spacing={2} alignItems="center">
						<Grid item xs={4}>
							<TextField
								label={t('inventory.article.itemsAddQuantity', 'Quantity')}
								value={!quantity ? '' : quantity}
								onChange={handleQuantityChange}
								fullWidth
							/>
						</Grid>
						<Grid item xs={newStatus === 'IN_USE' ? 4 : 8}>
							<TextField
								select
								label={t('inventory.article.itemsAddStatus', 'Item status')}
								value={newStatus}
								onChange={handleStatusChange}
								fullWidth
							>
								{inventoryStatusesList.map((status) => (
									<MenuItem key={status} value={status}>
										{getInventoryStatusTranslation(t, status)}
									</MenuItem>
								))}
							</TextField>
						</Grid>
						{newStatus === 'IN_USE' && (
							<Grid item xs={4}>
								<TextField
									id="type"
									label={t('inventory.columns.allocation', 'Allocation')}
									name="type"
									select
									value={allocationValue}
									onChange={(e) => setAllocationValue(e.target.value as AllocationOption)}
									margin="none"
									type="text"
									variant="outlined"
									fullWidth
								>
									{allocationOptionValues.map((allocation, idx) => {
										return (
											<MenuItem key={`${allocation}${idx}`} value={allocation}>
												{getInventoryAllocationOptionTranslation(t, allocation)}
											</MenuItem>
										);
									})}
								</TextField>
							</Grid>
						)}
					</Grid>
				</Box>
			</Box>
		</ActionDialog>
	);
};

export default AddItemsModal;
